import React, { useCallback, useEffect, useRef, useState } from 'react'
import MediaQuery from 'react-responsive'
import { useHistory } from 'react-router-dom'

import { AcornsIcon } from '@acorns/icons'
import {
  ActionStrip,
  ConfirmationModal,
  ModalActionTypes,
  PageDivider,
  Spinner,
} from '@acorns/web-components'
import { GenericAnalyticsClient, useRemoteStrings } from '@acorns/web-utils'

import { trackPortfolioButtonTappedPayload } from 'generated/trackingEvents'
import {
  InvestmentAccountReopenRequestState,
  Portfolio,
  PortfolioTheme,
  User,
  useInvestAccountQuery,
  useReopenUserAccountMutation,
  useRequestInvestmentAccountReopenMutation,
  useSubscribeToTierMutation,
} from 'generated/types'
import { usePersistedState } from 'hooks/usePersistedState'
import { OnboardingContent } from 'pages/invest/PortfolioContent'
import { ActionStripMobile } from 'pages/portfolio/ActionStripMobile'
import AboutYourPortfolio from 'pages/portfolio/ChangePortfolioTheme/preview-portfolio/AboutYourPortfolio'
import {
  Header,
  HeadingStyled,
  StyledGrid,
  StyledHeaderBodyText,
} from 'pages/portfolio/ChangePortfolioTheme/preview-portfolio/styled-components'
import YearProjectionOverview from 'pages/portfolio/components/YearProjectionOverview'
import usePortfolioData from 'pages/portfolio/hooks/usePortfolioData'
import usePortfolioHandlers from 'pages/portfolio/hooks/usePortfolioHandlers'
import { FormattedDisplayedTheme } from 'pages/portfolio/types'
import { routes } from 'router/routes'
import themeValues from 'theme'
import { reopenFlowTypeInReopenFunnel } from 'utils/analytics/reopenFlowAnalytics'
import { capitalizeFirstLetter } from 'utils/string/formatter'
import { PORTFOLIO_ETF } from 'utils/testing/testIDs'

import { GetStep } from './ClosedAccount'
import { pendingReopenButtonTapped } from './events'
import { REOPEN_FLOW_SUBSCRIPTION_KEY } from './PricingInterstitial'
import {
  ButtonContainer,
  CenteredSpinner,
  Container,
  StyledButton,
  StyledContent,
} from './styled-components'

interface Props {
  setStep: (index: number) => void
  user: User
  analytics: GenericAnalyticsClient
  getStep: GetStep
  isLongReopen?: boolean
}

export const PortfolioPreview = (props: Props) => {
  const history = useHistory()
  const reopenFlowType = reopenFlowTypeInReopenFunnel(props.isLongReopen)

  const { getRemoteString } = useRemoteStrings()
  const [selectedEtf, setSelectedEtf] = useState<number | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [subscriptionState] = usePersistedState(
    REOPEN_FLOW_SUBSCRIPTION_KEY,
    JSON.stringify({}),
    'localStorage',
  )

  const { data: investmentAccountData, loading } = useInvestAccountQuery()
  const [subscribeToTier, { loading: isSubscribing }] =
    useSubscribeToTierMutation()
  const [reopenCustomerAccount, { loading: isReopeningAccount }] =
    useReopenUserAccountMutation()
  const [reopenInvestAccount, { loading: isReopeningProductAccount }] =
    useRequestInvestmentAccountReopenMutation()
  const { routeParameters } = usePortfolioData({
    isLongReopen: props.isLongReopen,
  })

  const actionStripTitle = getRemoteString({
    key: 'registration.invest.toolbar',
  })
  const investAccount = investmentAccountData?.investmentAccountsByUserId?.[0]
  const graphData = (
    investAccount?.portfolio?.portfolioSecurityAllocations || []
  ).map(({ allocationPercent }) => allocationPercent) as number[]
  const portfolio = investAccount?.portfolio as Portfolio
  const { handleETFPress, actionStripActions } = usePortfolioHandlers({
    isProductInvest: true,
    isProductLater: false,
    isProductEarly: false,
    routeParameters,
    portfolio,
  })

  const header = (
    <Header>
      <HeadingStyled>
        {getRemoteString({
          key: 'portfolio.recommended.top_title.variable',
          variables: ['Invest'],
        })}
      </HeadingStyled>
      <StyledHeaderBodyText>
        {getRemoteString({ key: 'portfolio.recommended.top_description' })}
      </StyledHeaderBodyText>
    </Header>
  )
  const cta = getRemoteString({
    key: 'invest.onboarding.portfolio_recommendation.cta',
  })

  const subscribeAndReopen = useCallback(async () => {
    const subscriptionInput = JSON.parse(subscriptionState)
    // If kycInReopen was enabled on the Pricing screen, the mutations would have already been called and subscriptionState would be empty
    if (subscriptionInput.tierId && subscriptionInput.tierPriceId) {
      await subscribeToTier({
        variables: {
          input: {
            ...subscriptionInput,
            userId: props.user.uuid,
          },
        },
      })
      await reopenCustomerAccount()
    }
  }, [
    props.user.uuid,
    subscribeToTier,
    subscriptionState,
    reopenCustomerAccount,
  ])

  const hasRedirectedRef = useRef(false)

  useEffect(() => {
    if (hasRedirectedRef.current || loading || (investAccount && portfolio)) {
      return
    }

    if (!investAccount || !portfolio) {
      hasRedirectedRef.current = true
      const subscribeAndRedirect = async () => {
        await subscribeAndReopen()
        localStorage.removeItem(REOPEN_FLOW_SUBSCRIPTION_KEY)
        /**
         * Note the usage of history.go here.
         * This is being used since it appears to use default browser routing under the hood.
         * Typical routing methods like history.push or history.replace will not work when routing
         * to an async loaded route in AuthenticatedRouter.tsx since this page is rendered
         * before the async routes are registered.
         */
        history.go(routes.present)
      }

      subscribeAndRedirect()
    }
  }, [
    subscribeAndReopen,
    history,
    loading,
    investAccount,
    portfolio,
    subscribeToTier,
    props.user.uuid,
    subscriptionState,
    reopenCustomerAccount,
  ])

  if (loading || !investAccount || !portfolio) {
    return (
      <CenteredSpinner>
        <Spinner />
      </CenteredSpinner>
    )
  }

  const handleCtaPress = async () => {
    props.analytics.track(
      ...trackPortfolioButtonTappedPayload({
        ctaTitle: cta,
        funnel: 'reopen',
        type: reopenFlowType,
        investmentProduct: 'invest',
      }),
    )
    await subscribeAndReopen()
    const response = await reopenInvestAccount({
      variables: { accountId: investAccount.id },
    })
    localStorage.removeItem(REOPEN_FLOW_SUBSCRIPTION_KEY)
    if (
      response.data?.requestInvestmentAccountReopen.__typename ===
        'InvestmentAccountReopenRequest' &&
      response.data?.requestInvestmentAccountReopen.state !==
        InvestmentAccountReopenRequestState.Approved
    ) {
      setIsModalOpen(true)
    } else {
      props.setStep(props.getStep('Reopen'))
    }
  }
  const handleModalPress = () => {
    props.analytics.track('Button Tapped', pendingReopenButtonTapped)
    setIsModalOpen(false)
    history.go(routes.present)
  }

  return (
    <>
      <Container isPortfolioPage={true}>
        <MediaQuery maxWidth={themeValues.breakpoints.sm}>
          <ActionStripMobile
            title={actionStripTitle}
            handleAboutPress={actionStripActions[0].handler}
            aboutIcon={AcornsIcon.Icon.ProductGrow}
            showBackButton={false}
          />
        </MediaQuery>
        <MediaQuery minWidth={themeValues.breakpoints.sm + 1}>
          <ActionStrip
            maxWidth={800}
            showBack={false}
            title={actionStripTitle}
            actions={actionStripActions}
          />
        </MediaQuery>
        <StyledContent>
          <OnboardingContent
            header={header}
            graphData={graphData}
            portfolios={[portfolio]}
            portfolio={portfolio}
            setSelectedEtf={setSelectedEtf}
          />
          <AboutYourPortfolio
            portfolioRisk={
              capitalizeFirstLetter(investAccount.portfolio?.riskLevel)!
            }
            portfolioTheme={
              investAccount.portfolio?.theme === PortfolioTheme.Standard
                ? FormattedDisplayedTheme.STANDARD
                : FormattedDisplayedTheme.SUSTAINABLE
            }
            portfolioTitle={getRemoteString({
              key: 'portfolio.recommended.about.title',
            })}
            portfolioDescription={getRemoteString({
              key: `portfolio.recommended.about.description.${investAccount.portfolio?.riskLevel.toLowerCase()}`,
            })}
          />
        </StyledContent>
        <PageDivider thin={false} />
        <StyledGrid
          items={investAccount.portfolio?.portfolioSecurityAllocations || []}
          onItemPress={handleETFPress}
          hasBorder={false}
          hasHeader={true}
          selectedIndex={selectedEtf}
          hasPercentage={true}
          mobilePadding={0}
          testIdPrefix={PORTFOLIO_ETF}
          itemType="ETFs"
          title={getRemoteString({ key: 'portfolio.grid_list_header.title' })}
        />
        <PageDivider thin={false} />
        <YearProjectionOverview
          lowProjection={investAccount.portfolio?.projection!.lowestGrowthRate}
          highProjection={
            investAccount.portfolio?.projection!.highestGrowthRate
          }
          averageProjection={
            investAccount.portfolio?.projection!.averageGrowthRate
          }
          hasBorder={false}
        />
        <ButtonContainer>
          <StyledButton
            margin={'0 auto'}
            onPress={handleCtaPress}
            disabled={
              isSubscribing || isReopeningAccount || isReopeningProductAccount
            }
          >
            {cta}
          </StyledButton>
        </ButtonContainer>

        <ConfirmationModal
          isOpen={isModalOpen}
          onRequestClose={handleModalPress}
          title={getRemoteString({
            key: 'invest.onboarding.portfolio_recommendation.reopen.success.alert.title',
          })}
          message={getRemoteString({
            key: 'invest.onboarding.portfolio_recommendation.reopen.success.alert.body',
          })}
          actions={[
            {
              handler: handleModalPress,
              label: 'OK',
              type: ModalActionTypes.primary,
            },
          ]}
        />
      </Container>
    </>
  )
}
